import ChevronDownIcon from '@assets/icons/chevron-down.svg?react'
import ChevronUpIcon from '@assets/icons/chevron-up.svg?react'
import { CurrencyIcon } from '@components'
import cn from 'classnames'
import type { AccountBalanceResponse } from 'mmfintech-checkout-commons/types'
import { formatMoney, isValidArray, tr, useDropDownMenu } from 'mmfintech-commons'
import type React from 'react'
import { useEffect, useState } from 'react'
import { ThreeDots } from 'react-loader-spinner'

import './SelectAccount.scss'

interface SelectAccountProps {
  staticDisplay?: boolean
  accounts?: AccountBalanceResponse[]
  error?: string
  label?: string | React.ReactNode
  showAllAccounts?: boolean
  selectedAccount?: AccountBalanceResponse
  setSelectedAccount?: (account: AccountBalanceResponse) => void
  noShadow?: boolean
  hasExchangeAssets?: boolean
  className?: string
  noBorder?: boolean
  showId?: boolean
  loading?: boolean
}

export const SelectAccount = (props: SelectAccountProps) => {
  const {
    staticDisplay = false, // if set, static account display will be presented
    accounts, // list of accounts to select from
    error, // error message (if needed)
    label, // label above the selection box
    showAllAccounts = false, // if set, "All Account" option will be added at the beginning of the list
    selectedAccount, // selected account
    setSelectedAccount, // selected account setter
    noShadow = false,
    hasExchangeAssets = true,
    className,
    showId = false, // shows account id next to name
    noBorder = false, // hides border
    loading
  } = props

  const [preselectAccountId, setPreselectAccountId] = useState(0)

  const { visible, hideDropDown, toggleDropDown, setRef } = useDropDownMenu()
  const toggle = () => !staticDisplay && toggleDropDown()

  const handleSelect = (account: AccountBalanceResponse) => {
    if (selectedAccount?.id === account?.id) {
      // If clicking on already selected account, just close the dropdown
      hideDropDown()
    } else {
      // Select the new account (dropdown will close via useEffect)
      setSelectedAccount(account)
    }
  }

  const { balance, currencyCode, name, id } = selectedAccount || {}

  useEffect(() => {
    if (preselectAccountId) {
      const find = accounts.find(account => account.id === preselectAccountId)
      setPreselectAccountId(0)
      handleSelect(find)
    }
  }, [accounts])

  useEffect(() => {
    if (selectedAccount) {
      hideDropDown()
    }
  }, [selectedAccount])

  return (
    <div className={cn('select-account-wrapper', className)} data-test='account-dropdown' ref={setRef}>
      {label && <div className='select-account-label'>{label}</div>}
      <div className='select-account-container'>
        <div
          className={cn('account-button', {
            'no-shadow': noShadow,
            static: staticDisplay,
            error: error?.length,
            'no-border': noBorder
          })}
          onClick={toggle}
        >
          {loading ? (
            <span className='loading'>
              <ThreeDots height='12px' color='#1e63ff' />
            </span>
          ) : selectedAccount ? (
            <span className='account-info'>
              <span className='icon'>
                <CurrencyIcon currency={currencyCode} size='26px' />
              </span>
              <span className='content'>
                {id ? (
                  <span className='balance'>
                    <b>{tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.BALANCE_LABEL', 'Balance')}:</b>{' '}
                    {formatMoney(balance, currencyCode)}
                  </span>
                ) : null}
                {name && (
                  <span>
                    {name} {showId && id ? ` - ID: ${id}` : ''}
                  </span>
                )}
              </span>
            </span>
          ) : (
            <span className='no-account'>
              {showAllAccounts
                ? tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.ALL_ACCOUNTS', 'All Accounts')
                : tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.PLACEHOLDER', 'Select Account')}
            </span>
          )}
          {!staticDisplay && <span className='chevron'>{visible ? <ChevronUpIcon /> : <ChevronDownIcon />}</span>}
        </div>

        {!staticDisplay && (
          <div className={cn('account-list-wrapper', { visible })} data-test='accounts-menu-content'>
            {showAllAccounts && (
              <div className='account-styled' data-test='all-accounts-menu-item' onClick={() => handleSelect(null)}>
                <span className='icon' />
                <span className='content'>
                  <span className='balance'>
                    {tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.ALL_ACCOUNTS', 'All Accounts')}
                  </span>
                </span>
              </div>
            )}

            {isValidArray(accounts) ? (
              accounts.map(account => {
                const { id, currencyCode, balance, name: accName } = account

                return (
                  <div
                    key={id}
                    className='account-styled option'
                    onClick={() => handleSelect(account)}
                    data-test='account-menu-item'
                  >
                    <span className='icon'>
                      <CurrencyIcon currency={currencyCode} size='26px' />
                    </span>
                    <span className='content'>
                      <span className='balance'>
                        {tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.BALANCE_LABEL', 'Balance')}:{' '}
                        {formatMoney(balance, currencyCode)}
                      </span>
                      <span>{accName}</span>
                    </span>
                  </div>
                )
              })
            ) : (
              <div className='account-styled disabled' data-test='all-accounts-menu-item'>
                <span className='icon'>
                  <span className='icon-all'>N/A</span>
                </span>
                <span className='content'>
                  <span className='balance'>
                    <b>
                      {hasExchangeAssets
                        ? tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.NO_ACCOUNTS', 'No eligible accounts')
                        : tr(
                            'FRONTEND.COMPONENTS.SELECT_ACCOUNT.CANNOT_EXCHANGE',
                            'Currently this asset cannot be exchanged'
                          )}
                    </b>
                  </span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {error?.length && (
        <div className='select-account-error' data-test='error-wrapper'>
          {error}
        </div>
      )}
    </div>
  )
}
